import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import GET_PRODUCT_CACHE from 'graph/product-cache/product-cache-query';
import {
  App,
  Product,
  ProductCurriculum,
  ProductFamily,
  ProductType,
  AppProduct,
  AppAccessDetails,
  AppPurchaseDetails
} from 'types/schema-generated-types';

type ProductCacheEntities = {
  products: Product[];
  productFamilies: ProductFamily[];
  apps: App[];
};

export type PliManagedApp = {
  title: string;
  description?: string;
  type?: ProductType;
  accessDetails?: AppAccessDetails;
  purchaseDetails?: AppPurchaseDetails;
  app: App;
  appProduct?: AppProduct;
};

type Maybe<T> = T | undefined | null;

const transformApp = (app: App): PliManagedApp => ({
  app,
  title: app.title ?? app.name,
  ...(app.description && { description: app.description }),
  ...(app.type && { type: app.type }),
  ...(app.accessDetails && { accessDetails: app.accessDetails }),
  ...(app.purchaseDetails && { purchaseDetails: app.purchaseDetails })
});

const transformAppProduct = (app: App, appProduct: AppProduct): PliManagedApp => ({
  app,
  appProduct,
  title: appProduct.title ?? appProduct.product?.code!,
  ...(appProduct.description && { description: appProduct.description }),
  ...(app.type && { type: app.type }),
  ...(appProduct.accessDetails && { accessDetails: appProduct.accessDetails }),
  ...(appProduct.purchaseDetails && { purchaseDetails: appProduct.purchaseDetails })
});

export const transformApps = (apps: App[]): PliManagedApp[] => {
  const result: PliManagedApp[] = [];

  apps.filter(isAppVisible).forEach(app => {
    const appProducts = app.appProducts?.filter(isAppVisible) ?? [];
    if (appProducts.length === 0) {
      result.push(transformApp(app));
      return;
    }

    appProducts.forEach(appProduct => {
      result.push(transformAppProduct(app, appProduct));
    });
  });

  return result;
};

export type ProductCacheState = ProductCacheEntities & {
  isLoading: boolean;
  isError: boolean;

  // PLI-specific selectors
  getApps: () => PliManagedApp[];
  getPliProducts: () => Product[];
  getPliProductFamilies: () => ProductFamily[];
  getPliProductFamiliesByType: (type: ProductType) => ProductFamily[];
  getPliProductsByType: (type: ProductType) => Product[];
  // App selectors
  findAppByProductCode: (productCode: string) => Maybe<PliManagedApp>;
  // Product Family selectors
  findProductFamilyByCode: (code: string) => Maybe<ProductFamily>;
  findProductFamilyById: (id: number) => Maybe<ProductFamily>;
  findProductFamilyByProductCode: (code: string) => Maybe<ProductFamily>;
  findProductFamilyByProductCurriculumCode: (code: string) => Maybe<ProductFamily>;
  // Product selectors
  findProductByProductCode: (productCode: string) => Maybe<Product>;
  findProductByProductFamilyName: (productFamilyName: string) => Maybe<Product>;
  findProductById: (productId: number) => Maybe<Product>;
  findProductByName: (name: string) => Maybe<Product>;
  findProductsByProductFamilyCode: (productFamilyCode: string) => Product[];
  findProductsByProductFamilyCodeAndType: (
    productFamilyCode: string,
    type: ProductType
  ) => Product[];
  findProductsByProductFamilyId: (productFamilyId: number) => Product[];
  findProductsByProductFamilyIdAndType: (productFamilyId: number, type: ProductType) => Product[];
  findProductByProductCurriculumId: (id: number) => Maybe<Product>;
  // Product curriculum selectors
  findProductCurriculumByCode: (code: string) => Maybe<ProductCurriculum>;
  findProductCurriculumById: (id: number) => Maybe<ProductCurriculum>;
};

export type ProductCache = [ProductCacheState, undefined];

const isAppVisible = (x: App | AppProduct): boolean => x.pliManaged;
const isProductVisible = (x: Product): boolean | null | undefined => x.family?.pliManaged;
const isProductFamilyVisible = (x: ProductFamily): boolean => !!x?.pliManaged;
const isProductOfType = (type: ProductType) => (x: Product): boolean => x.type === type;
const isProductFamilyOfType = (type: ProductType) => (x: ProductFamily): boolean =>
  x.types.includes(type);

const useProductCache = (): ProductCache => {
  const {
    data: { products, productFamilies, apps } = {
      products: [],
      productFamilies: [],
      apps: []
    } as ProductCacheEntities,
    loading,
    error
  } = useQuery(GET_PRODUCT_CACHE);

  const getPliProducts = (): Product[] => {
    return products.filter(isProductVisible);
  };

  const getPliProductsByType = (type: ProductType): Product[] => {
    return products.filter(isProductVisible).filter(isProductOfType(type));
  };

  const getPliProductFamilies = useCallback((): ProductFamily[] => {
    return productFamilies.filter(isProductFamilyVisible);
  }, [productFamilies]);

  const getPliProductFamiliesByType = useCallback(
    (type: ProductType): ProductFamily[] => {
      return getPliProductFamilies().filter(isProductFamilyOfType(type));
    },
    [getPliProductFamilies]
  );

  const getApps = useCallback((): PliManagedApp[] => {
    return transformApps(apps).sort((a: PliManagedApp, b: PliManagedApp) => {
      if (a.title && b.title) return a.title.localeCompare(b.title);
      if (!a.title) return 1;
      if (!b.title) return -1;
      return 0;
    });
  }, [apps]);

  const findProductByProductCode = useCallback(
    (productCode: string) => {
      return products.find(x => x.code === productCode && isProductVisible(x));
    },
    [products]
  );

  const findProductById = useCallback(
    (productId: number) => {
      return products.find(x => x.id === productId && isProductVisible(x));
    },
    [products]
  );

  const findProductByName = (name: string): Maybe<Product> => {
    return products.find(x => x.name === name && isProductVisible(x));
  };

  const findProductByProductFamilyName = (productFamilyName: string): Maybe<Product> => {
    return products.find(x => x.family?.name === productFamilyName && isProductVisible(x));
  };

  const findProductsByProductFamilyCode = (productFamilyCode: string): Product[] => {
    return products.filter(x => x.family?.code === productFamilyCode && isProductVisible(x));
  };

  const findProductsByProductFamilyCodeAndType = (
    productFamilyCode: string,
    type: ProductType
  ): Product[] => {
    return products.filter(
      x => x.family?.code === productFamilyCode && x.type === type && isProductVisible(x)
    );
  };

  const findProductsByProductFamilyId = (productFamilyId: number): Product[] => {
    return products.filter(x => x.family?.id === productFamilyId && isProductVisible(x));
  };

  const findProductsByProductFamilyIdAndType = (
    productFamilyId: number,
    type: ProductType
  ): Product[] => {
    return products.filter(
      x => x.family?.id === productFamilyId && x.type === type && isProductVisible(x)
    );
  };

  const findProductFamilyById = (id: number): Maybe<ProductFamily> => {
    return productFamilies.find(x => x.id === id && isProductFamilyVisible(x));
  };

  const findProductFamilyByCode = (code: string): Maybe<ProductFamily> => {
    return productFamilies.find(x => x.code === code && isProductFamilyVisible(x));
  };

  const findProductFamilyByProductCode = (code: string): Maybe<ProductFamily> => {
    const product = products.find(x => x.code === code && isProductVisible(x));

    return product?.family;
  };

  const findAppByProductCode = (productCode: string): Maybe<PliManagedApp> => {
    const apps = getApps();
    const app = apps.find(a => a.appProduct?.product?.code === productCode);
    if (app) return app;

    return apps.find(a => a.app.products?.some(p => p.code === productCode));
  };

  // TODO: This curriculum stuff below is super hacky for now - will be fixed when I get time to refactor this entire product cache hook.
  const findProductCurriculumByCode = (code: string): Maybe<ProductCurriculum> => {
    const productCurriculums = products.flatMap(product => product.curriculums ?? []);
    return productCurriculums.find(c => c.code === code);
  };

  const findProductCurriculumById = (id: number): Maybe<ProductCurriculum> => {
    const productCurriculums = products.flatMap(product => product.curriculums ?? []);
    return productCurriculums.find(c => c.id === id);
  };

  const findProductByProductCurriculumId = (id: number): Maybe<Product> => {
    const productCurriculums = products.flatMap(product => product.curriculums ?? []);
    const productCurriculum = productCurriculums.find(c => c.id === id);

    if (!productCurriculum) return null;

    return products.find(p => p.id === productCurriculum.productId);
  };

  const findProductFamilyByProductCurriculumCode = (code: string): Maybe<ProductFamily> => {
    const productCurriculum = findProductCurriculumByCode(code);
    const product = productCurriculum && findProductById(productCurriculum.productId);
    return product && findProductFamilyByProductCode(product.code);
  };

  return [
    {
      isLoading: loading,
      isError: !!error,
      apps,
      productFamilies,
      products,
      getApps,
      getPliProductFamilies,
      getPliProductFamiliesByType,
      getPliProducts,
      getPliProductsByType,
      findAppByProductCode,
      findProductFamilyByCode,
      findProductFamilyById,
      findProductFamilyByProductCode,
      findProductFamilyByProductCurriculumCode,
      findProductById,
      findProductByName,
      findProductByProductCode,
      findProductByProductCurriculumId,
      findProductsByProductFamilyCode,
      findProductByProductFamilyName,
      findProductsByProductFamilyCodeAndType,
      findProductsByProductFamilyId,
      findProductsByProductFamilyIdAndType,
      findProductCurriculumByCode,
      findProductCurriculumById
    },
    undefined
  ];
};

export default useProductCache;
